// frontend/src/components/InlineFilters.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { fetchCompanyFilters } from "../api.js";
import { FILTER_OPTIONS } from "../filterCConstants.js";

// ✅ FIXED: CSS variables for light/dark mode support
const C = {
  text: "var(--text-primary)",
  subtext: "var(--text-secondary)",
  fieldBg: "var(--surface)",
  fieldBorder: "var(--border-primary)",
  fieldHover: "var(--surface-hover)",
  chipBg: "rgba(96, 165, 250, 0.15)",
  chipBorder: "rgba(96, 165, 250, 0.35)",
  chipText: "var(--accent-primary)",
  menuBg: "var(--surface)",
  menuBorder: "var(--border-primary)",
  menuHover: "var(--surface-hover)",
  badge: "#2563EB",
  badgeText: "#fff",
  btn: "#2563EB",
  btnHover: "#1D4ED8",
  btnSubtle: "#6B7280",
  btnSubtleHover: "#4B5563",
  inputOutline: "#3B82F6",
};

const TITLE = (s) =>
  (s || "")
    .toString()
    .trim()
    .replace(/[_\-]+/g, " ")
    .replace(/\s+/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const PRETTY_STATUS = (raw) => {
  const k = (raw || "").toString().toLowerCase();
  const statusItem = FILTER_OPTIONS.STATUSES.find((s) => s.value === k);
  if (statusItem) return statusItem.label;
  const map = {
    pending_gov_approval: "Pending",
  };
  return map[k] || TITLE(raw);
};

const PRETTY_MANAGED_BY = (raw) => {
  const k = (raw || "").toString().toLowerCase();
  const item = FILTER_OPTIONS.MANAGED_BY.find((m) => m.value === k);
  return item ? item.label : TITLE(raw);
};

const prettyLabel = (domain, raw) => {
  if (domain === "status") return PRETTY_STATUS(raw);
  if (domain === "managed_by") return PRETTY_MANAGED_BY(raw);
  return TITLE(raw);
};

const normalizeList = (arr, domain) => (arr || []).map((x) => (typeof x === "string" ? { value: x, label: prettyLabel(domain, x) } : x));

function Chip({ label, onRemove }) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 10px",
        borderRadius: 999,
        fontSize: 14,
        fontWeight: 600,
        background: C.chipBg,
        border: `1px solid ${C.chipBorder}`,
        color: C.chipText,
      }}>
      <span>{label}</span>
      <button
        onClick={onRemove}
        type="button"
        aria-label="Remove filter"
        style={{
          width: 22,
          height: 22,
          display: "grid",
          placeItems: "center",
          borderRadius: 999,
          background: "transparent",
          border: "none",
          color: C.chipText,
          cursor: "pointer",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = C.menuHover)}
        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
        ✕
      </button>
    </div>
  );
}

function Dropdown({ id, label, options, selected, onSelect, searchable, type = "checkbox", openKey, setOpenKey }) {
  const open = openKey === id;
  const [term, setTerm] = useState("");

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpenKey(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, setOpenKey]);

  const count = Array.isArray(selected) ? selected.length : selected ? 1 : 0;

  const filtered = useMemo(() => {
    if (!searchable || !term) return options || [];
    const q = term.toLowerCase();
    return (options || []).filter((o) => (typeof o === "string" ? o : o.label)?.toLowerCase().includes(q));
  }, [options, term, searchable]);

  return (
    <div className="ifb-dropdown" style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpenKey(open ? null : id)}
        className="ifb-btn"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          height: 40,
          padding: "0 12px",
          borderRadius: 8,
          border: `1px solid ${count ? C.chipBorder : C.fieldBorder}`,
          background: count ? C.chipBg : C.fieldBg,
          color: count ? C.chipText : C.text,
          fontSize: 14,
          fontWeight: 600,
          cursor: "pointer",
        }}
        onMouseEnter={(e) => {
          if (!count) e.currentTarget.style.background = C.fieldHover;
        }}
        onMouseLeave={(e) => {
          if (!count) e.currentTarget.style.background = C.fieldBg;
        }}>
        <span>{label}</span>
        {!!count && (
          <span
            style={{
              background: C.badge,
              color: C.badgeText,
              fontSize: 12,
              lineHeight: 1,
              padding: "3px 7px",
              borderRadius: 999,
              minWidth: 18,
              textAlign: "center",
            }}>
            {count}
          </span>
        )}
        <span
          style={{
            marginLeft: 4,
            fontSize: 12,
            opacity: 0.8,
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform .15s",
          }}>
          ▼
        </span>
      </button>

      {open && (
        <>
          <div onClick={() => setOpenKey(null)} style={{ position: "fixed", inset: 0, zIndex: 10 }} />
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              marginTop: 4,
              width: 256,
              maxHeight: 320,
              background: C.menuBg,
              color: C.text,
              border: `1px solid ${C.menuBorder}`,
              borderRadius: 8,
              boxShadow: "0 12px 32px rgba(0,0,0,.35),0 6px 16px rgba(0,0,0,.25)",
              overflow: "hidden",
              zIndex: 500,
            }}>
            {searchable && (
              <div style={{ padding: 12, borderBottom: `1px solid ${C.menuBorder}` }}>
                <div style={{ position: "relative" }}>
                  <span
                    style={{
                      position: "absolute",
                      left: 12,
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: C.subtext,
                    }}>
                    🔍
                  </span>
                  <input
                    value={term}
                    onChange={(e) => setTerm(e.target.value)}
                    placeholder="Search…"
                    style={{
                      width: "100%",
                      padding: "8px 10px 8px 36px",
                      borderRadius: 6,
                      border: `1px solid ${C.fieldBorder}`,
                      background: C.fieldBg,
                      color: C.text,
                      outline: "none",
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.outline = `2px solid ${C.inputOutline}`;
                      e.currentTarget.style.outlineOffset = "2px";
                    }}
                    onBlur={(e) => (e.currentTarget.style.outline = "none")}
                  />
                </div>
              </div>
            )}
            <div style={{ maxHeight: 240, overflowY: "auto" }}>
              {(filtered || []).map((opt) => {
                const value = typeof opt === "string" ? opt : opt.value;
                const text = typeof opt === "string" ? opt : opt.label;
                const isSelected = Array.isArray(selected) ? selected.includes(value) : selected === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => onSelect(value)}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "10px 14px",
                      background: "transparent",
                      border: "none",
                      color: C.text,
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = C.menuHover)}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                    {type === "checkbox" ? (
                      <div
                        style={{
                          width: 16,
                          height: 16,
                          borderRadius: 4,
                          border: `1px solid ${isSelected ? C.badge : C.fieldBorder}`,
                          background: isSelected ? C.badge : "transparent",
                          display: "grid",
                          placeItems: "center",
                        }}>
                        {isSelected && (
                          <span
                            style={{
                              color: "#fff",
                              fontSize: 10,
                              fontWeight: 900,
                              transform: "translateY(-1px)",
                            }}>
                            ✓
                          </span>
                        )}
                      </div>
                    ) : (
                      <div
                        style={{
                          width: 16,
                          height: 16,
                          borderRadius: "50%",
                          border: `1px solid ${isSelected ? C.badge : C.fieldBorder}`,
                          background: isSelected ? C.badge : "transparent",
                          display: "grid",
                          placeItems: "center",
                        }}>
                        {isSelected && <div style={{ width: 8, height: 8, background: "#fff", borderRadius: "50%" }} />}
                      </div>
                    )}
                    <span style={{ flex: 1 }}>{text}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function InlineFilters({ visible, initial = {}, onApply }) {
  const containerRef = useRef(null);
  const [options, setOptions] = useState({});
  const [local, setLocal] = useState(initial);
  const [openKey, setOpenKey] = useState(null);

  useEffect(() => {
    if (!visible) return;
    const onDown = (e) => {
      if (!containerRef.current?.contains(e.target)) setOpenKey(null);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    (async () => {
      try {
        const raw = (await fetchCompanyFilters()) || {};
        setOptions({
          risk: FILTER_OPTIONS.RISK_LEVELS,
          status: FILTER_OPTIONS.STATUSES,
          legal_form: FILTER_OPTIONS.LEGAL_FORMS,
          residency_status: FILTER_OPTIONS.RESIDENCY_STATUS,
          managed_by: raw.managed_by || FILTER_OPTIONS.MANAGED_BY,
          countries: raw.countries || [],
        });
      } catch (e) {
        console.error(e);
        setOptions({
          risk: FILTER_OPTIONS.RISK_LEVELS,
          status: FILTER_OPTIONS.STATUSES,
          legal_form: FILTER_OPTIONS.LEGAL_FORMS,
          residency_status: FILTER_OPTIONS.RESIDENCY_STATUS,
          managed_by: FILTER_OPTIONS.MANAGED_BY,
          countries: [],
        });
      }
    })();
  }, [visible]);

  useEffect(() => {
    if (visible) setLocal(initial || {});
  }, [visible, initial]);

  const toggle = (key, val) =>
    setLocal((prev) => {
      const set = new Set(prev[key] || []);
      set.has(val) ? set.delete(val) : set.add(val);
      return { ...prev, [key]: [...set] };
    });

  const setManagedBy = (val) =>
    setLocal((prev) => ({
      ...prev,
      managed_by: prev.managed_by === val ? "" : val,
    }));

  const chips = useMemo(() => {
    const rows = [];
    const push = (label, remover) => rows.push({ label, remover });

    const labelOf = (key) =>
      ({
        risk: "Risk",
        status: "Status",
        legal_form: "Company Type",
        residency_status: "Residency",
        pca_country_id: "Country",
        managed_by: "Managed by",
      })[key] || key;

    Object.entries(local).forEach(([key, value]) => {
      if (!value || (Array.isArray(value) && value.length === 0)) return;

      if (Array.isArray(value)) {
        value.forEach((v) => {
          let text = v;
          if (key === "pca_country_id") {
            const match = options.countries?.find((c) => c.value === v);
            text = match?.label || v;
          } else {
            const domainList = options[key] || [];
            const match = domainList.find((o) => (typeof o === "string" ? o === v : o.value === v));
            text = (typeof match === "string" ? prettyLabel(key, match) : match?.label) || prettyLabel(key, v);
          }
          push(`${labelOf(key)}: ${text}`, () => toggle(key, v));
        });
      } else if (key === "managed_by") {
        const match = options.managed_by?.find((m) => m.value === value);
        const text = match?.label || prettyLabel(key, value);
        push(`${labelOf(key)}: ${text}`, () => setManagedBy(value));
      }
    });

    return rows;
  }, [local, options]);

  if (!visible) return null;

  return (
    <section
      ref={containerRef}
      className="ifb"
      style={{ marginTop: 12, marginBottom: 14, padding: 14, borderRadius: 12, background: "rgba(148,163,184,.06)", border: `1px solid ${C.fieldBorder}` }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
        <div style={{ color: C.subtext, display: "flex", alignItems: "center", gap: 6 }}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
          </svg>
        </div>

        <Dropdown
          id="risk"
          label="Risk Level"
          options={options.risk || []}
          selected={local.risk || []}
          onSelect={(v) => toggle("risk", v)}
          openKey={openKey}
          setOpenKey={setOpenKey}
        />
        <Dropdown
          id="status"
          label="Status"
          options={options.status || []}
          selected={local.status || []}
          onSelect={(v) => toggle("status", v)}
          openKey={openKey}
          setOpenKey={setOpenKey}
        />
        <Dropdown
          id="legal"
          label="Company Type"
          options={options.legal_form || []}
          selected={local.legal_form || []}
          onSelect={(v) => toggle("legal_form", v)}
          openKey={openKey}
          setOpenKey={setOpenKey}
        />
        <Dropdown
          id="residency"
          label="Residency"
          options={options.residency_status || []}
          selected={local.residency_status || []}
          onSelect={(v) => toggle("residency_status", v)}
          openKey={openKey}
          setOpenKey={setOpenKey}
        />
        <Dropdown
          id="managed_by"
          label="Managed by"
          options={options.managed_by || []}
          selected={local.managed_by || ""}
          onSelect={setManagedBy}
          type="radio"
          openKey={openKey}
          setOpenKey={setOpenKey}
        />
        {(options.countries?.length || 0) > 0 && (
          <Dropdown
            id="country"
            label="Country"
            options={options.countries}
            selected={local.pca_country_id || []}
            onSelect={(v) => toggle("pca_country_id", v)}
            searchable
            openKey={openKey}
            setOpenKey={setOpenKey}
          />
        )}
      </div>

      {chips.length > 0 && (
        <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          <span style={{ color: C.subtext, fontWeight: 700 }}>Active:</span>
          {chips.map((c, i) => (
            <Chip key={i} label={c.label} onRemove={c.remover} />
          ))}
          <button type="button" onClick={() => setLocal({})} style={{ marginLeft: 8, background: "none", border: "none", color: C.badge, fontWeight: 700, cursor: "pointer" }}>
            Clear all
          </button>
        </div>
      )}

      <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button
          type="button"
          onClick={() => {
            setLocal({});
            onApply?.({});
          }}
          style={{ background: C.btnSubtle, color: "#fff", border: "none", borderRadius: 10, padding: "10px 18px", fontWeight: 800, cursor: "pointer" }}
          onMouseEnter={(e) => (e.currentTarget.style.background = C.btnSubtleHover)}
          onMouseLeave={(e) => (e.currentTarget.style.background = C.btnSubtle)}>
          Clear Filter
        </button>
        <button
          type="button"
          onClick={() => onApply?.(local)}
          style={{ background: C.btn, color: "#fff", border: "none", borderRadius: 10, padding: "10px 18px", fontWeight: 800, cursor: "pointer" }}
          onMouseEnter={(e) => (e.currentTarget.style.background = C.btnHover)}
          onMouseLeave={(e) => (e.currentTarget.style.background = C.btn)}>
          Apply Filters
        </button>
      </div>
    </section>
  );
}
